import {
  AuthErrorBuilder,
  AuthErrorCategory,
  AuthErrorDomain,
  AuthErrorNumeral,
  AuthErrorSeverity,
} from '../AuthError'
import type { ClientlessAPI } from '../ClientlessAPI'
import {
  ZipCheckEnvironmentName,
} from '../auth/common/configuration/AuthConfiguration'
import type {
  AuthConfiguration,
  ZipCheckEnvironment,
} from '../auth/common/configuration/AuthConfiguration'
import { MetadataKey } from '../engine/AuthEngineTypes'

export interface ZipCodeFencingRequest {
  mvpd: string
  zipcode: string
  network: string
}

export interface ZipCodeFencingResponse {
  mvpd: string
  zipcode: string
  network: string
  blocked: boolean
}

const ZIP_CHECK_PATH = '/token/geolocation/zipcheck'
const TOKEN_SERVICE_ORIGINS: Record<ZipCheckEnvironment, string> = {
  [ZipCheckEnvironmentName.PROD]: 'https://token.ngtv.io',
  [ZipCheckEnvironmentName.ITE]: 'https://ite.token.ngtv.io',
  [ZipCheckEnvironmentName.REF]: 'https://ref.token.ngtv.io',
}

export class ZipCodeFencingService {
  async check(
    clientlessApi: ClientlessAPI,
    channel: string,
    authConfig: AuthConfiguration
  ): Promise<void> {
    const mvpdId = clientlessApi.getMvpdID()
    if (!mvpdId) {
      console.warn('[ZipCodeFencingService] Missing MVPD; skipping check')
      return
    }

    const { value, encrypted } = clientlessApi.requestUserMetadataEntry(MetadataKey.ZIP) ?? {}
    if (encrypted) {
      console.debug('[ZipCodeFencingService] ZIP code is encrypted; skipping check')
      return
    }

    const zip = this.normalizeZipCode(value)
    if (!zip) {
      console.debug('[ZipCodeFencingService] Profile has no ZIP code; skipping check')
      return
    }

    const response = await this.requestDecision({
      mvpd: mvpdId,
      zipcode: zip,
      network: channel,
    }, authConfig)

    if (response?.blocked) {
      throw new AuthErrorBuilder()
        .withSeverity(AuthErrorSeverity.Error)
        .withCategory(AuthErrorCategory.Authorization)
        .withDomain(AuthErrorDomain.Shared)
        .withMessage('Authorization denied for this location')
        .withNumeral(AuthErrorNumeral.ZipCodeNotAuthorized)
        .withSubErrorCode('ZIP_CODE_FENCED')
        .build()
    }
  }

  private normalizeZipCode(value: unknown): string | undefined {
    const values = Array.isArray(value) ? value : [value]
    const zip = values.find((candidate): candidate is string =>
      typeof candidate === 'string' && candidate.trim().length > 0
    )
    return zip?.trim()
  }

  private async requestDecision(
    request: ZipCodeFencingRequest,
    authConfig: AuthConfiguration
  ): Promise<ZipCodeFencingResponse | undefined> {
    const environment = authConfig.zipCheckEnvironment ?? ZipCheckEnvironmentName.PROD

    let response: Response
    try {
      const query = [
        `mvpd=${encodeURIComponent(request.mvpd)}`,
        `zipcode=${encodeURIComponent(request.zipcode)}`,
        `network=${encodeURIComponent(request.network)}`,
      ].join('&')
      const url = `${TOKEN_SERVICE_ORIGINS[environment]}${ZIP_CHECK_PATH}?${query}`
      response = await fetch(url)
    } catch (error) {
      console.error(`[ZipCodeFencingService] Service unavailable; continuing authorization: ${error}`)
      return
    }

    if (!response.ok) {
      console.error(`[ZipCodeFencingService] Request failed with status ${response.status}; continuing authorization`)
      return
    }

    let data: unknown
    try {
      data = await response.json()
    } catch {
      console.error('[ZipCodeFencingService] Invalid response; continuing authorization')
      return
    }

    if (!this.isZipCodeFencingResponse(data)) {
      console.error('[ZipCodeFencingService] Invalid response; continuing authorization')
      return
    }

    return data
  }

  private isZipCodeFencingResponse(response: unknown): response is ZipCodeFencingResponse {
    if (!response || typeof response !== 'object') return false

    const result = response as Partial<ZipCodeFencingResponse>
    return typeof result.mvpd === 'string'
      && typeof result.zipcode === 'string'
      && typeof result.network === 'string'
      && typeof result.blocked === 'boolean'
  }
}
